// The draft of an RFQ as it will go out: recipient, lines, commercial terms and
// the mail preview, edited in place and sent from here. Shown inside the
// conversation while the RFQ is a draft.
// apps/smart-catalog/frontend/src/components/buyer-agent/rfq-center/RfqDraftComposer.tsx

import React, { useCallback, useMemo, useState, useEffect, useRef, useDeferredValue } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  Chip,
  Skeleton,
  Alert,
  alpha,
  useTheme,
  Link as MuiLink,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Checkbox,
  Popover,
} from '@mui/material';
import {
  Inventory2Outlined as PartIcon,
  AccountTreeOutlined as BomIcon,
  Storefront as SupplierIcon,
  EditNote as DraftIcon,
  Send as SentIcon,
  CheckCircleOutlined as ReceivedIcon,
  ThumbUpAlt as AcceptedIcon,
  Cancel as RejectedIcon,
  Description as DescIcon,
  Receipt as RfqIcon,
  WarningAmberOutlined as StaleIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Reply as ReplyIcon,
  ForwardOutlined as ForwardIcon,
  HubOutlined as WorkspaceIcon,
} from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import suppliersService from '../../../services/suppliersService';
import { useQuery } from '@tanstack/react-query';
import rfqPolicyService from '../../../services/rfqPolicyService';
import { useBreadcrumbUpdater } from '@ciea.ai/smart-infra-navigation';

import buyerAgentService, { RfqDocument } from '../../../services/buyerAgentService';
import bomV2Service from '../../../services/bomV2Service';
import RfqLineItemsEditor, {
  RfqLineItemsEditorValue,
  emptyLineItemsEditorValue,
  toLineItemOverrides,
} from '../RfqLineItemsEditor';
import CommercialTermsBar, {
  effectiveCommercialTerms,
  CommercialTerms,
} from '../CommercialTermsBar';
import { EmailHtmlFrame } from '../../EmailHtmlFrame';
import { boundsOf, findMarks, FoundMark, MarkBox, markValue, stripMarks } from './emailMarks';
import { LETTER_FIELDS, briefVariables, letterVariables, quantityText } from './rfqLetter';

// ── DraftPreviewPanel ──────────────────────────────────────────────────────
// Right-pane content when the RFQ is in DRAFT / PENDING_REVIEW. Shows what's
// about to be sent + a single prominent "Review & Send" action that opens
// the full review dialog.

type DraftEditor = 'lines' | 'terms' | 'wording';
/** Template slots that are not wording: the system or the Terms tab owns them. */
const NOT_WORDING = new Set(['rfqReference', 'replyByDate', 'commercialTermsLine', 'validityDays', 'currency', 'incoterms', 'paymentTerms', ...LETTER_FIELDS]);
const WORDING_LABEL: Record<string, string> = {
  contactName: 'Greeting',
  introLine: 'Opening line',
  requestLine: 'What we ask for',
  closingLine: 'Closing line',
  additionalNotes: 'Note to the supplier',
  senderName: 'Signed by',
  senderTitle: 'Title',
  companyName: 'Company',
  companyAddress: 'Address',
  senderPhone: 'Phone',
};
type SpotKind = 'text' | 'terms' | 'lines';
/** One editable piece of the email. Its position in the list is the number its mark carries. */
interface Spot {
  kind: SpotKind;
  /** The template field, or `quantity:<row>` / `targetPrice:<row>` for a table cell. */
  name?: string;
}
/** A mark carries a 7-bit number: wording fields, two for the terms, two per line. */
const MAX_MARKED_LINES = 50;

interface Recipient {
  email: string;
  name?: string;
  type?: 'TO' | 'CC' | 'BCC';
}

export interface DraftPreviewPanelHandle {
  send: () => void;
}

interface DraftPreviewPanelProps {
  rfq: RfqDocument;
  /** Tells the host page whether the draft is sendable, so the host can
   *  render an aligned "Send to …" button in the page's status strip
   *  alongside the SENT-state "Reply / Forward" buttons. */
  onSendableChange?: (state: { canSend: boolean; sending: boolean }) => void;
  /** Called once the RFQ has gone out, so the host can show the conversation. */
  onSent?: () => void;
}

type ApplyScope = 'line' | 'lines' | 'bom';

/**
 * Terms and wording set on one offer are meant for more than one: the same
 * line's other offers, other lines, or the whole BOM. Applying keeps them on
 * those lines (new drafts start with them) and updates their open drafts.
 * The greeting is per supplier and is not shared.
 */
function ApplyTo({ rfqId, commercialTerms, wording }: { rfqId: string; commercialTerms: CommercialTerms; wording: Record<string, string> }) {
  const theme = useTheme();
  const qc = useQueryClient();
  const [scope, setScope] = useState<ApplyScope>('line');
  const [picked, setPicked] = useState<string[]>([]);
  const scopeQuery = useQuery({ queryKey: ['rfq-shared-settings', rfqId], queryFn: () => rfqPolicyService.getSharedSettingsScope(rfqId), retry: false });
  const apply = useMutation({
    mutationFn: () => rfqPolicyService.applySharedSettings(rfqId, { commercialTerms, wording, scope, lineItemIds: scope === 'lines' ? picked : undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rfq-center'] });
      qc.invalidateQueries({ queryKey: ['rfq-shared-settings', rfqId] });
    },
  });
  const own = scopeQuery.data?.lines.find((l) => l.lineItemId === scopeQuery.data?.lineItemId);
  const others = scopeQuery.data?.lines.filter((l) => l.lineItemId !== scopeQuery.data?.lineItemId) ?? [];
  // A draft that is not tied to a BOM line has nothing to share with.
  if (!scopeQuery.data || !own) return null;
  const options: Array<{ key: ApplyScope; label: string }> = [
    { key: 'line', label: own.partNumber ? `This line · ${own.partNumber}` : 'This line' },
    ...(others.length ? [{ key: 'lines' as const, label: 'Other lines' }, { key: 'bom' as const, label: 'Entire BOM' }] : []),
  ];
  const targets = scope === 'bom' ? [own, ...others] : scope === 'lines' ? [own, ...others.filter((l) => picked.includes(l.lineItemId))] : [own];
  const drafts = targets.reduce((sum, l) => sum + l.openDrafts, 0);
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const summary =
    scope === 'lines' && picked.length === 0
      ? 'Choose the lines'
      : `${targets.length > 1 ? `${plural(targets.length, 'line')} · ` : ''}${plural(drafts, 'open draft')}`;
  return (
    <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
      <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}>
          Apply to
        </Typography>
        <Box role="radiogroup" aria-label="Apply to" sx={{ display: 'inline-flex', p: 0.375, borderRadius: 999, border: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper' }}>
          {options.map((o) => {
            const on = scope === o.key;
            return (
              <Box
                key={o.key}
                component="button"
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => { setScope(o.key); apply.reset(); }}
                sx={{
                  font: 'inherit',
                  border: 0,
                  cursor: 'pointer',
                  px: 1.75,
                  py: 0.5,
                  borderRadius: 999,
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  bgcolor: on ? 'primary.main' : 'transparent',
                  color: on ? 'primary.contrastText' : 'text.secondary',
                  transition: 'background-color 120ms, color 120ms',
                  '&:hover': { color: on ? 'primary.contrastText' : 'text.primary' },
                  '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
                }}
              >
                {o.label}
              </Box>
            );
          })}
        </Box>
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{summary}</Typography>
        <Box sx={{ flex: 1 }} />
        {apply.isSuccess && (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'success.main' }}>
            <CheckIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Applied</Typography>
          </Stack>
        )}
        {apply.isError && <Typography sx={{ fontSize: '0.8rem', color: 'error.main' }}>Could not apply</Typography>}
        <Button
          size="small"
          variant="contained"
          disableElevation
          disabled={apply.isPending || (scope === 'lines' && picked.length === 0)}
          onClick={() => apply.mutate()}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 999, px: 2.25, minWidth: 0 }}
        >
          {apply.isPending ? 'Applying' : 'Apply'}
        </Button>
      </Stack>
      {scope === 'lines' && (
        <Box sx={{ mt: 1.5, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, bgcolor: 'background.paper', overflow: 'hidden', maxWidth: 760 }}>
          {others.map((l, index) => {
            const on = picked.includes(l.lineItemId);
            return (
              <Box
                key={l.lineItemId}
                component="label"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1,
                  py: 0.25,
                  cursor: 'pointer',
                  borderTop: index ? `1px solid ${theme.palette.divider}` : 0,
                  bgcolor: on ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                }}
              >
                <Checkbox size="small" checked={on} onChange={() => { setPicked(on ? picked.filter((id) => id !== l.lineItemId) : [...picked, l.lineItemId]); apply.reset(); }} />
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, flexShrink: 0 }}>{l.partNumber ?? 'Line'}</Typography>
                <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.description}</Typography>
                {l.openDrafts > 0 && <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', flexShrink: 0, pr: 1 }}>{plural(l.openDrafts, 'open draft')}</Typography>}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

export const DraftPreviewPanel = React.forwardRef<DraftPreviewPanelHandle, DraftPreviewPanelProps>(({
  rfq,
  onSendableChange,
  onSent,
}, ref) => {
  const theme = useTheme();
  const qc = useQueryClient();

  // ── Editable state — all live in this panel; no dialog ──
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [editRecipients, setEditRecipients] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  // Phase 1.1 — per-line edits (keep/remove/qty/target). Local-only until
  // Send; applied server-side via lineItemOverrides on the sendRfq call.
  const [lineEdits, setLineEdits] = useState<RfqLineItemsEditorValue>(emptyLineItemsEditorValue);
  // Phase 1.2 — commercial terms. Pre-fill from the persisted rfq.commercialTerms
  // if present; tenant defaults layered in by the CommercialTermsBar itself.
  const [commercialTerms, setCommercialTerms] = useState<CommercialTerms>(
    (rfq.commercialTerms as CommercialTerms) || {},
  );
  // Per-template-variable overrides. The user types into a field → key set
  // here, value layered on top of server-provided defaults when the preview
  // re-renders. Empty string is treated as "no override" so clearing a
  // field falls back to the server default rather than blanking the email.
  const [fieldOverrides, setFieldOverrides] = useState<Record<string, string>>(rfq.draftWording ?? {});
  // The email is the editor: each editable piece is a click area drawn over
  // the rendered email, and clicking one opens its editor right there.
  const [marks, setMarks] = useState<FoundMark[]>([]);
  const [open, setOpen] = useState<{ kind: SpotKind; name?: string; top: number; left: number } | null>(null);
  // Kept in state, not a ref: the frame appears once the email has loaded, and the measuring starts then.
  const [frameWrap, setFrameWrap] = useState<HTMLDivElement | null>(null);
  const frameDoc = useRef<Document | null>(null);
  const onDocument = useCallback((doc: Document) => {
    frameDoc.current = doc;
    setMarks(findMarks(doc));
  }, []);
  // The email reflows when the panel changes width: measure again.
  useEffect(() => {
    const el = frameWrap;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      if (frameDoc.current) setMarks(findMarks(frameDoc.current));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [frameWrap]);
  const [sendError, setSendError] = useState<string | null>(null);

  // Send-time context (template slug + server-derived vars + optional drawing).
  const sendCtxQuery = useQuery({
    queryKey: ['rfq-send-context', rfq._id],
    queryFn: () => buyerAgentService.getRfqSendContext(rfq._id),
    enabled: !!rfq._id,
    staleTime: 60_000,
  });
  const templateSlug = sendCtxQuery.data?.templateSlug || 'rfq-initial';
  const serverVars = sendCtxQuery.data?.variables || {};
  const drawingAttachment = sendCtxQuery.data?.drawingAttachment;
  // The RFQ letter: delivery, terms and signature from the company sourcing profile.
  const letterCtx = sendCtxQuery.data?.letter ?? null;
  const briefs = sendCtxQuery.data?.requirementsBriefs;
  const sellerNumbers = sendCtxQuery.data?.supplierPartNumbers;

  // Pull the template definition for its `variables[]` schema — that's the
  // list of editable slots ("contactName", "additionalNotes", "senderName",
  // etc.). Non-string types (e.g. the parts array) are excluded from the
  // form; those come from the line items themselves.
  const templatesQuery = useQuery({
    queryKey: ['email-templates'],
    queryFn: () => buyerAgentService.getTemplates(),
    staleTime: 5 * 60_000,
  });
  const templateDef = (templatesQuery.data || []).find(
    (t) => t.slug === templateSlug,
  );
  const editableVars = (templateDef?.variables || []).filter(
    (v) => v.type === 'string',
  );

  const wordingVars = editableVars.filter((v) => !NOT_WORDING.has(v.name));

  const deferredOverrides = useDeferredValue(fieldOverrides);
  const deferredLineEdits = useDeferredValue(lineEdits);
  // The email states the terms that apply: the defaults unless the buyer changed them.
  const effectiveTerms = useMemo(() => effectiveCommercialTerms(commercialTerms, letterCtx?.defaultTerms), [commercialTerms, letterCtx]);
  const deferredTerms = useDeferredValue(effectiveTerms);
  // Compose the effective line items the email will be rendered against:
  // (1) start from rfq.lineItems, (2) drop any marked for removal, (3) apply
  // qty/target/notes overrides. The live preview updates as the buyer types
  // so there's no "Apply" button — what you see is what gets sent.
  const effectiveLineItems = useMemo(() => {
    const removed = new Set(deferredLineEdits.removedIds);
    return (rfq.lineItems || [])
      .filter((p) => !removed.has(p.lineItemId))
      .map((p) => {
        const o = deferredLineEdits.overrides[p.lineItemId];
        if (!o) return p;
        return {
          ...p,
          requestedQuantity: o.requestedQuantity ?? p.requestedQuantity,
          targetPrice: o.targetPrice ?? p.targetPrice,
          targetCurrency: o.targetCurrency ?? p.targetCurrency,
          notes: o.notes !== undefined ? o.notes : (p as any).notes,
        };
      });
  }, [rfq.lineItems, deferredLineEdits]);

  // The RFQ letter's values (delivery, terms, reply-by, signature, subject):
  // from the company sourcing profile, the lines and the terms as edited now.
  const letterParts = useMemo(
    () => effectiveLineItems.map((p) => ({
      manufacturer: p.manufacturerName || '',
      mpn: p.manufacturerPartNumber || '',
      partNumber: p.partNumber || '',
      quantity: quantityText(Number(p.requestedQuantity ?? 1), p.unit),
    })),
    [effectiveLineItems],
  );
  const letterValues = useMemo<Record<string, string>>(
    () => (letterCtx ? letterVariables(letterCtx.profile, letterParts, deferredTerms) : {}),
    [letterCtx, letterParts, deferredTerms],
  );

  // Single source of truth for "what value would be sent if the user changes
  // nothing?". Both the per-field form (valueFor) and the live preview
  // (previewVars) read from this so the displayed form value and the rendered
  // email body never disagree. Layering, lowest priority first:
  //   1. Template defaultValue
  //   2. serverVars from getRfqSendContext
  //   3. Situational (rfq.partnerName for contactName, rfq.rfqNumber for
  //      rfqReference) — derived from THIS RFQ, beats generic server defaults.
  const computedDefaults = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const v of editableVars) {
      if (v.defaultValue) out[v.name] = v.defaultValue;
    }
    for (const [k, v] of Object.entries(serverVars)) {
      if (v != null && v !== '') out[k] = String(v);
    }
    // The letter's values beat the template defaults: the profile signs the email.
    for (const [k, v] of Object.entries(letterValues)) {
      if (v) out[k] = v;
    }
    if (rfq.partnerName) out.contactName = rfq.partnerName;
    if (rfq.rfqNumber) out.rfqReference = rfq.rfqNumber;
    return out;
  }, [editableVars, serverVars, letterValues, rfq.partnerName, rfq.rfqNumber]);

  const valueFor = (name: string): string => {
    const override = fieldOverrides[name];
    if (override !== undefined) return override;
    return computedDefaults[name] ?? '';
  };
  const setVar = (name: string, val: string) =>
    setFieldOverrides((prev) => ({ ...prev, [name]: val }));

  // Pre-fill recipients from the supplier record (email + contacts[]) on mount.
  const supplierQuery = useQuery({
    queryKey: ['supplier-for-rfq', rfq.partnerId],
    queryFn: () => suppliersService.getById(rfq.partnerId!),
    enabled: !!rfq.partnerId,
    staleTime: 60_000,
  });
  useEffect(() => {
    const s: any = supplierQuery.data;
    if (!s) return;
    const seen = new Set<string>();
    const pre: Recipient[] = [];
    const push = (email?: string, name?: string) => {
      const e = (email ?? '').trim().toLowerCase();
      if (!e || seen.has(e)) return;
      seen.add(e);
      pre.push({ email: email!.trim(), name, type: 'TO' });
    };
    push(s.email, s.supplierName || rfq.partnerName);
    const primary = (s.contacts ?? []).find((c: any) => c.isPrimary && c.email);
    push(primary?.email, primary?.name);
    for (const c of s.contacts ?? []) push(c.email, c.name);
    setRecipients(pre);
  }, [supplierQuery.data, rfq.partnerName]);

  // Build template variables. Uses computedDefaults so the preview shows the
  // same value the form shows — typing into a field updates the preview, and
  // an untouched field doesn't blank out a defaulted slot in the email.
  // Layering: computedDefaults (defaults+server+situational) → user overrides.
  // `parts` is an array (line items) and lives outside the form.
  const previewVars = useMemo(() => {
    const merged: Record<string, any> = {
      ...computedDefaults,
      parts: effectiveLineItems.map((p, index) => ({
        itemNo: index + 1,
        partNumber: p.partNumber || 'N/A',
        manufacturerPartNumber: p.manufacturerPartNumber || '',
        mpn: p.manufacturerPartNumber || '',
        manufacturer: p.manufacturerName || '',
        description: p.description || '',
        // The supplier's own number, as the send adds it.
        supplierPartNumber: sellerNumbers?.[p.lineItemId] ?? '',
        quantity: letterCtx ? quantityText(Number(p.requestedQuantity ?? 1), p.unit) : `${p.requestedQuantity} ${p.unit || 'EA'}`,
        // Phase 1.2 — per-line targetPrice + notes flow into the template's
        // `parts` array so the body table can render them.
        targetPrice: typeof p.targetPrice === 'number' ? p.targetPrice : '',
        targetCurrency: p.targetCurrency || '',
        notes: (p as any).notes || '',
        // A line that asks by requirements: the requirements, as the letter states them.
        brief: briefs?.[p.lineItemId] ?? p.requirementsBrief ?? undefined,
      })),
    };
    Object.assign(merged, briefVariables(merged.parts));
    // Phase 1.2 — Top-level commercial-terms template variables. Mirrors the
    // controller's send-side composition so the live preview matches the
    // actual outgoing email. Templates with #if guards on these vars render
    // or skip the terms block based on what the buyer has set.
    if (deferredTerms.replyByDate) {
      // Format ISO date for display (matches controller-side formatting)
      try {
        const d = new Date(deferredTerms.replyByDate);
        merged.replyByDate = !isNaN(d.getTime())
          ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : deferredTerms.replyByDate;
      } catch {
        merged.replyByDate = deferredTerms.replyByDate;
      }
    }
    if (typeof deferredTerms.validityDays === 'number') {
      merged.validityDays = deferredTerms.validityDays;
    }
    if (deferredTerms.currency) merged.currency = deferredTerms.currency;
    if (deferredTerms.incoterms) merged.incoterms = deferredTerms.incoterms;
    if (deferredTerms.paymentTerms) merged.paymentTerms = deferredTerms.paymentTerms;
    // Pre-format the commercial-terms summary line. Same formula as the
    // controller (currency · incoterms · paymentTerms · "Quote validity Nd").
    const termParts: string[] = [];
    if (deferredTerms.currency) termParts.push(deferredTerms.currency);
    if (deferredTerms.incoterms) termParts.push(deferredTerms.incoterms);
    if (deferredTerms.paymentTerms) termParts.push(deferredTerms.paymentTerms);
    if (typeof deferredTerms.validityDays === 'number') {
      termParts.push(`Quote validity ${deferredTerms.validityDays} days`);
    }
    if (termParts.length > 0) {
      merged.commercialTermsLine = termParts.join(' · ');
    }
    for (const [k, v] of Object.entries(deferredOverrides)) {
      if (v != null && v !== '') merged[k] = v;
    }
    return merged;
  }, [computedDefaults, effectiveLineItems, deferredOverrides, deferredTerms, briefs, sellerNumbers]);

  // What can be changed by clicking on the email, in a fixed order: the order is each piece's number.
  const wordingNames = wordingVars.map((v) => v.name).join('|');
  const lineCount = Math.min(effectiveLineItems.length, MAX_MARKED_LINES);
  const spots = useMemo<Spot[]>(() => {
    const list: Spot[] = wordingNames ? wordingNames.split('|').map((name) => ({ kind: 'text' as const, name })) : [];
    if (letterCtx) list.push({ kind: 'terms', name: 'deliveryLine' }, { kind: 'terms', name: 'termsLine' }, { kind: 'terms', name: 'replyByLong' });
    else list.push({ kind: 'terms', name: 'replyByDate' }, { kind: 'terms', name: 'commercialTermsLine' });
    for (let i = 0; i < lineCount; i += 1) list.push({ kind: 'lines', name: `quantity:${i}` }, { kind: 'lines', name: `targetPrice:${i}` });
    return list;
  }, [wordingNames, lineCount, letterCtx]);
  // The same variables the email is sent with, each editable value wrapped so
  // it can be found in the rendered preview. Used for the preview only.
  const markedVars = useMemo(() => {
    const out: Record<string, any> = { ...previewVars, parts: (previewVars.parts as Array<Record<string, any>>).map((part) => ({ ...part })) };
    spots.forEach((spot, id) => {
      if (spot.kind === 'lines') {
        const [field, index] = (spot.name as string).split(':');
        const part = out.parts[Number(index)];
        if (part && part[field] !== '' && part[field] != null) part[field] = markValue(id, String(part[field]));
      } else if (spot.name && typeof out[spot.name] === 'string' && out[spot.name]) {
        out[spot.name] = markValue(id, out[spot.name]);
      }
    });
    return out;
  }, [previewVars, spots]);

  const previewQuery = useQuery({
    queryKey: ['rfq-template-preview', templateSlug, markedVars],
    queryFn: () => buyerAgentService.previewTemplate(templateSlug, markedVars),
    enabled: !!previewVars && !!templateSlug,
    staleTime: 60_000,
    // The email stays on screen while it re-renders after an edit.
    placeholderData: (previous) => previous,
  });

  const subject = stripMarks(previewQuery.data?.subject || '') || `Request for Quotation — ${rfq.rfqNumber}`;
  const bodyHtml = previewQuery.data?.bodyHtml;
  const bodyLoading = sendCtxQuery.isLoading || previewQuery.isLoading;

  // Direct send — no dialog. On success → React Query invalidates → next
  // render lands on the SENT state, which routes to ThreadDetailView.
  const sendMutation = useMutation({
    mutationFn: () => buyerAgentService.sendRfq(rfq._id, {
      recipients: recipients.map((r) => ({ email: r.email, name: r.name, type: r.type ?? 'TO' })),
      templateSlug,
      templateVariables: Object.fromEntries(
        Object.entries(fieldOverrides).filter(([, v]) => v != null && v !== ''),
      ),
      // Phase 1.1 — pass per-line edits so the server applies + persists them
      // before rendering the template. Empty array when buyer made no edits.
      lineItemOverrides: toLineItemOverrides(lineEdits, rfq.lineItems || []),
      // The terms the preview shows are the terms that go out and are kept on the RFQ.
      commercialTerms: effectiveTerms,
    }),
    onSuccess: () => {
      setSendError(null);
      qc.invalidateQueries({ queryKey: ['rfq-detail-page', rfq._id] });
      qc.invalidateQueries({ queryKey: ['rfqs'] });
      onSent?.();
    },
    onError: (err: any) => {
      setSendError(err?.response?.data?.message || err?.message || 'Send failed');
    },
  });

  const addEmail = () => {
    const e = newEmail.trim();
    if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return;
    if (recipients.some((r) => r.email.toLowerCase() === e.toLowerCase())) {
      setNewEmail('');
      return;
    }
    setRecipients((prev) => [...prev, { email: e, type: 'TO' }]);
    setNewEmail('');
  };
  const removeRecipient = (email: string) =>
    setRecipients((prev) => prev.filter((r) => r.email !== email));

  const noRecipients = recipients.length === 0;
  const changed: Record<DraftEditor, boolean> = {
    lines: lineEdits.removedIds.length > 0 || Object.keys(lineEdits.overrides).length > 0,
    terms: JSON.stringify(commercialTerms) !== JSON.stringify((rfq.commercialTerms as CommercialTerms) || {}),
    wording: JSON.stringify(fieldOverrides) !== JSON.stringify(rfq.draftWording ?? {}),
  };
  const changedCount = Object.values(changed).filter(Boolean).length;
  const undoAll = () => {
    setLineEdits(emptyLineItemsEditorValue());
    setCommercialTerms((rfq.commercialTerms as CommercialTerms) || {});
    setFieldOverrides(rfq.draftWording ?? {});
  };
  const isSpotChanged = (spot: Spot): boolean =>
    spot.kind === 'terms' ? changed.terms : spot.kind === 'lines' ? changed.lines : (fieldOverrides[spot.name ?? ''] ?? '') !== (rfq.draftWording?.[spot.name ?? ''] ?? '');
  /** Open an editor at a piece of the email. `box` is in the email frame's own coordinates. */
  const openAt = (kind: SpotKind, name: string | undefined, box: MarkBox) => {
    const frame = frameWrap?.getBoundingClientRect();
    setOpen({ kind, name, top: (frame?.top ?? 0) + box.top + box.height + 4, left: (frame?.left ?? 0) + box.left });
  };
  const hasEmptyNote = wordingNames.split('|').includes('additionalNotes') && !valueFor('additionalNotes').trim();

  // Expose the send action to the host so the page status strip's Send
  // button can fire it. The mutation owns sending the email; this handle
  // is just a thin trigger.
  React.useImperativeHandle(
    ref,
    () => ({ send: () => sendMutation.mutate() }),
    [sendMutation],
  );

  // Notify the host whenever the sendable state changes so it can disable
  // the page-level Send button until a recipient is added.
  useEffect(() => {
    onSendableChange?.({
      canSend: !noRecipients,
      sending: sendMutation.isPending,
    });
  }, [noRecipients, sendMutation.isPending, onSendableChange]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 0,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: 'visible',
      }}
    >
      {/* Internal DRAFT status bar removed — the page-level status strip
          already shows the "Draft / Created today" pill, and the "Send to
          <supplier>" button now lives in that same strip so its position
          matches the SENT-state Reply / Forward buttons (single canonical
          location for all primary thread actions). */}

      {/* Email header strip — gmail-style To / Subject rows, inline editable */}
      <Box sx={{ p: 2.5, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Stack spacing={1.25} sx={{ flex: 1, minWidth: 240 }}>
          {/* ── To row — chips + inline editor ── */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Typography variant="caption" color="text.secondary" sx={{ width: 60, flexShrink: 0, fontSize: '0.72rem', pt: 0.5 }}>
              To
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {!editRecipients ? (
                <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                  {recipients.length === 0 ? (
                    <Typography variant="caption" sx={{ color: 'warning.dark', fontStyle: 'italic' }}>
                      No recipient yet. Add one
                    </Typography>
                  ) : (
                    recipients.map((r) => (
                      <Chip
                        key={r.email}
                        size="small"
                        label={r.name ? `${r.name} <${r.email}>` : r.email}
                        sx={{ height: 22, fontSize: '0.72rem' }}
                      />
                    ))
                  )}
                  <Tooltip title="Edit recipients">
                    <IconButton size="small" onClick={() => setEditRecipients(true)} sx={{ p: 0.25 }}>
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ) : (
                <Box>
                  <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap" sx={{ mb: 0.75 }}>
                    {recipients.map((r) => (
                      <Chip
                        key={r.email}
                        size="small"
                        label={r.name ? `${r.name} <${r.email}>` : r.email}
                        onDelete={() => removeRecipient(r.email)}
                        sx={{ height: 22, fontSize: '0.72rem' }}
                      />
                    ))}
                  </Stack>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <TextField
                      size="small"
                      placeholder="contact@supplier.com"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                      sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.5 } }}
                    />
                    <Tooltip title="Add">
                      <IconButton size="small" onClick={addEmail} disabled={!newEmail.trim()}>
                        <AddIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Done">
                      <IconButton size="small" onClick={() => { setEditRecipients(false); setNewEmail(''); }}>
                        <CheckIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              )}
            </Box>
          </Box>

          {/* ── Subject row (read-only — comes from template) ── */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography variant="caption" color="text.secondary" sx={{ width: 60, flexShrink: 0, fontSize: '0.72rem' }}>
              Subject
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {subject}
            </Typography>
          </Box>

          {/* ── Attachment row: the RFQ's own file list when it has one, else the drawing of a custom part ── */}
          {/* Always shown, so the buyer sees what goes with the email, also when nothing does. */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography variant="caption" color="text.secondary" sx={{ width: 60, flexShrink: 0, fontSize: '0.72rem' }}>
              Attach
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minWidth: 0, alignItems: 'baseline' }}>
              {rfq.packageManifest?.length ? (
                rfq.packageManifest.map((f) => (
                  <Chip key={f.documentId} size="small" label={`📎 ${f.fileName || f.documentType}`} sx={{ height: 22, fontSize: '0.72rem', maxWidth: '100%' }} />
                ))
              ) : !rfq.packageManifest && drawingAttachment?.willAttach ? (
                <Chip
                  size="small"
                  label={`📎 Drawing ${drawingAttachment.drawingRef || ''}${drawingAttachment.drawingRev ? ` rev ${drawingAttachment.drawingRev}` : ''}`}
                  sx={{ height: 22, fontSize: '0.72rem' }}
                />
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                  No files
                </Typography>
              )}
            </Box>
          </Box>

        </Stack>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
          {hasEmptyNote && (
            <Button
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setOpen({ kind: 'text', name: 'additionalNotes', top: r.bottom + 4, left: r.left });
              }}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              Add a note
            </Button>
          )}
          {changedCount > 0 && (
            <Button size="small" onClick={undoAll} sx={{ textTransform: 'none', color: 'text.secondary' }}>
              Undo changes
            </Button>
          )}
        </Stack>
      </Box>

      {/* Body preview — actual rendered template */}
      <Box
        sx={{
          p: 3,
          minHeight: 320,
          bgcolor: '#fff',
        }}
      >
        {bodyLoading && (
          <Stack spacing={1}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" />
            <Skeleton variant="text" />
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="rectangular" height={120} sx={{ mt: 1, borderRadius: 1 }} />
            <Skeleton variant="text" width="60%" />
          </Stack>
        )}
        {previewQuery.error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Couldn't render the email preview. The draft will still send correctly — open the
            Send dialog to inspect raw template variables.
          </Alert>
        )}
        {bodyHtml && (
          <Box ref={setFrameWrap} sx={{ position: 'relative' }}>
          <EmailHtmlFrame
            html={bodyHtml}
            onDocument={onDocument}
            extraCss={`
              body { font-size: 0.9rem; line-height: 1.55; color: ${theme.palette.text.primary}; }
              p { margin: 1em 0; }
              table { border-collapse: collapse; width: 100%; margin: 1.5em 0; }
              th, td { border: 1px solid ${theme.palette.divider}; padding: 0.75em; font-size: 0.85rem; }
              th { background: ${alpha(theme.palette.text.primary, 0.03)}; font-weight: 600; text-align: left; }
            `}
          />
          {/* One click area per editable piece, with a small pencil after it. */}
          {marks.map((mark) => {
            const spot = spots[mark.id];
            if (!spot) return null;
            const box = boundsOf(mark.boxes);
            const last = mark.boxes[mark.boxes.length - 1];
            return (
              <React.Fragment key={mark.id}>
              {/* A changed piece is underlined line by line, so a text over two lines reads as two straight strokes. */}
              {isSpotChanged(spot) &&
                mark.boxes.map((b, n) => (
                  <Box key={n} sx={{ position: 'absolute', left: b.left, top: b.top + b.height - 1, width: b.width, height: '1.5px', bgcolor: 'warning.main', opacity: 0.75, pointerEvents: 'none' }} />
                ))}
              <Box
                component="button"
                type="button"
                aria-label={`Change ${spot.kind === 'text' ? WORDING_LABEL[spot.name ?? ''] ?? humanizeVar(spot.name ?? '') : spot.kind === 'terms' ? 'terms' : 'lines'}`}
                onClick={() => openAt(spot.kind, spot.name, box)}
                sx={{
                  position: 'absolute',
                  left: box.left - 3,
                  top: box.top - 1,
                  width: box.width + 22,
                  height: box.height + 2,
                  p: 0,
                  border: 0,
                  borderRadius: 0.75,
                  cursor: 'pointer',
                  bgcolor: 'transparent',
                  transition: 'background-color 100ms',
                  '& svg': { position: 'absolute', left: last.left - box.left + last.width + 7, top: last.top - box.top + (last.height - 11) / 2 + 1, fontSize: 11, color: 'text.secondary', opacity: 0.4, transition: 'opacity 100ms' },
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.07) },
                  '&:hover svg': { opacity: 1, color: 'primary.main' },
                  '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
                }}
              >
                <EditIcon />
              </Box>
              </React.Fragment>
            );
          })}
          </Box>
        )}
      </Box>

      {/* Terms and wording changed here can be used for more than this one offer. */}
      {rfq.bomId && (changed.terms || changed.wording) && (
        <Box sx={{ px: 3, pb: 2 }}>
          <ApplyTo rfqId={rfq._id} commercialTerms={effectiveTerms} wording={fieldOverrides} />
        </Box>
      )}

      {/* The editor of the piece that was clicked, opened where it sits. */}
      <Popover
        open={!!open}
        onClose={() => setOpen(null)}
        anchorReference="anchorPosition"
        anchorPosition={open ? { top: open.top, left: open.left } : undefined}
        slotProps={{ paper: { sx: { p: 2, borderRadius: 2, maxWidth: 'min(760px, calc(100vw - 32px))' } } }}
      >
        {open?.kind === 'text' && open.name && (
          <Stack spacing={1.25} sx={{ width: 'min(440px, calc(100vw - 64px))' }}>
            <TextField
              autoFocus
              size="small"
              label={WORDING_LABEL[open.name] ?? humanizeVar(open.name)}
              multiline={/notes|address|paragraph|message|Line$/i.test(open.name)}
              minRows={open.name === 'additionalNotes' ? 2 : 1}
              maxRows={6}
              value={valueFor(open.name)}
              onChange={(e) => setVar(open.name as string, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !/notes|address|paragraph|message|Line$/i.test(open.name as string)) setOpen(null);
              }}
              InputLabelProps={{ shrink: true }}
            />
            <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
              <Button size="small" variant="contained" disableElevation onClick={() => setOpen(null)} sx={{ textTransform: 'none', borderRadius: 999 }}>
                Done
              </Button>
            </Stack>
          </Stack>
        )}
        {open?.kind === 'terms' && (
          <Stack spacing={1.25}>
            <CommercialTermsBar value={commercialTerms} onChange={setCommercialTerms} supplierName={rfq.partnerName} defaults={letterCtx?.defaultTerms} embedded />
            <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
              <Button size="small" variant="contained" disableElevation onClick={() => setOpen(null)} sx={{ textTransform: 'none', borderRadius: 999 }}>
                Done
              </Button>
            </Stack>
          </Stack>
        )}
        {open?.kind === 'lines' && (
          <Stack spacing={1.25}>
            <RfqLineItemsEditor baseLineItems={rfq.lineItems || []} value={lineEdits} onChange={setLineEdits} embedded />
            <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
              <Button size="small" variant="contained" disableElevation onClick={() => setOpen(null)} sx={{ textTransform: 'none', borderRadius: 999 }}>
                Done
              </Button>
            </Stack>
          </Stack>
        )}
      </Popover>

      {sendError && (
        <Alert
          severity="error"
          onClose={() => setSendError(null)}
          sx={{ borderRadius: 0 }}
        >
          {sendError}
        </Alert>
      )}
    </Paper>
  );
});

DraftPreviewPanel.displayName = 'DraftPreviewPanel';

// camelCase → "Camel Case" — used as field labels in the per-variable editor.
// Falls back to the raw name if it's already non-camelCase.
function humanizeVar(name: string): string {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());
}

// ── Small helper — key/value row used in the left-column cards ──────────────

const KvRow: React.FC<{ label: string; value: string; valueBold?: boolean }> = ({
  label, value, valueBold,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={valueBold ? 700 : 500}
      sx={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
    </Typography>
  </Box>
);

